// @ts-nocheck
import React, {useState} from 'react';
import {Box, Card, CardContent, CardHeader, Divider, IconButton, MenuItem, Select, TextField, Typography, Chip} from '@mui/material';
import {KeyboardArrowDown} from '@mui/icons-material';
import {t} from 'ttag';
import {useFormContext, useWatch} from 'react-hook-form';
import Amount from './Amount';
import {DataGrid} from '@mui/x-data-grid';
import {Delete} from '@mui/icons-material';
import AddRowBtn from './AddRowButton';
import ConfirmDialog from '@components/common/ConfirmDialog';
import CurrentValueTextField from '../../../../../../../../common/CurrentValueTextField';
import table from '../../../../../../../../../../components/common/styles';
import {MANUAL_TAX_TYPES} from '../../taxTypes';

const readOnlyColumns = [
  {
    field: 'taxCode',
    headerName: t`Code`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    cellClassName: 'secondary-text'
  },
  {
    field: 'value',
    headerName: t`Value`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({value}) => <Amount {...value} />,
    cellClassName: 'bold'
  },
  {
    field: 'currentValue',
    headerName: t`Current value`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({value}) => <Amount {...value} />,
    cellClassName: 'bold'
  },
  {
    field: 'type',
    headerName: t`Type`,
    width: 100,
    flex: 1,
    sortable: false,
    disableColumnMenu: true
  }
];

const cellSx = {'& .MuiInputBase-input': {fontSize: '0.875rem', pl: 1.15, pt: 0.7}};

const TAX_TYPES = [
  'totalCancellationFee',
  'totalFareUsed',
  'totalAmount',
  'totalPaidTaxes',
  'totalFarePaid',
  'totalFareRefund',
  'totalFareUsedWithCancellationFee',
  'totalUsedTaxesWithNonRefTaxes',
  'totalNonRefTaxes',
  'totalUsedTaxes',
  'totalRefund',
  'totalTaxRefund',
  'totalTaxes',
  'equivalentFare',
  'baseFare',
  'unusedTaxes',
  'nonRefTaxes',
  'paidTaxes',
  'refundableTaxes'
];

const EMPTY_NON_REFUNDABLE_TAX = {type: '', taxCode: '', value: 0, currentValue: 0, active: true, modified: true};

const RefundSubjectTaxes = ({editable = true}) => {
  const {setValue} = useFormContext();
  const [currencyCode, taxes, manual] = useWatch({name: ['currencyCode', 'taxes', 'manual']});
  const [taxToDelete, setTaxToDelete] = useState(null);

  if (!taxes) return null;

  if (manual) {
    const nonRefundableTaxes = taxes.filter(tx => tx.active !== false);

    const editHandler = ({field, value, id}) => {
      setValue('taxes', taxes.map(tx => tx.localId === id ? {...tx, [field]: value, modified: true} : tx), {shouldValidate: true});
    };

    const createHandler = () => {
      setValue('taxes', taxes.concat([{...EMPTY_NON_REFUNDABLE_TAX, localId: taxes.length, currency: currencyCode}]), {shouldValidate: true});
    };

    const removeHandler = () => {
      setValue('taxes', taxes.filter(tx => tx.localId !== taxToDelete.localId), {shouldValidate: true});
      setTaxToDelete(null);
    };

    const editableColumns = [
      {
        field: 'taxCode',
        headerName: t`Code`,
        editable: true,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: 'bold secondary-text',
        valueGetter: (params) => params.row.taxCode || '',
        renderEditCell: (params) => (
          <TextField
            variant='standard'
            size='small'
            value={params.value}
            inputProps={{maxLength: 2}}
            InputProps={{disableUnderline: true}}
            sx={cellSx}
            autoFocus
            fullWidth
            onChange={(e) => {
              const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
              params.api.setEditCellValue({id: params.id, field: params.field, value: val});
              editHandler({id: params.id, field: 'taxCode', value: val});
            }}
          />
        ),
      },
      {
        field: 'value',
        type: 'number',
        headerName: t`Value`,
        editable: false,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => <Amount value={row.value} currency={row.currency} />,
        cellClassName: 'bold',
      },
      {
        field: 'currentValue',
        type: 'number',
        headerName: t`Current value`,
        editable: true,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({row}) => <Amount value={row.taxCode ? (row.currentValue || 0) : undefined} currency={row.currency} />,
        cellClassName: 'secondary-text bold',
        renderEditCell: (params) => <CurrentValueTextField params={params} editHandler={editHandler} />,
      },
      {
        field: 'type',
        headerName: t`Type`,
        editable: true,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (params) => params.row.type || '',
        renderCell: (params) => {
          const hasError = !params.value;
          return (
            <Box
              display='flex'
              alignItems='center'
              gap={0.5}
              onClick={() => editable && params.api.startCellEditMode({id: params.id, field: params.field})}
              sx={{
                cursor: editable ? 'pointer' : 'default',
                width: '100%',
                border: hasError ? '1px solid' : 'none',
                borderColor: hasError ? 'error.main' : 'transparent',
                borderRadius: 1,
                px: hasError ? 0.5 : 0,
              }}
            >
              <Typography variant='body2' sx={{fontSize: '0.875rem', color: hasError ? 'error.main' : 'inherit', flexGrow: 1}}>
                {params.value || t`Required`}
              </Typography>
              <KeyboardArrowDown fontSize='small' sx={{color: hasError ? 'error.main' : 'text.secondary', fontSize: '1rem'}} />
            </Box>
          );
        },
        renderEditCell: (params) => (
          <Select
            value={params.value || ''}
            size='small'
            variant='standard'
            disableUnderline
            autoFocus
            fullWidth
            sx={{'& .MuiInputBase-input': {fontSize: '0.875rem', pl: 1.15}}}
            onChange={(e) => {
              params.api.setEditCellValue({id: params.id, field: params.field, value: e.target.value});
              editHandler({id: params.id, field: 'type', value: e.target.value});
            }}
          >
            {MANUAL_TAX_TYPES.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        ),
      },
      {
        field: 'actions',
        headerName: t`Actions`,
        flex: 0.5,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box>
            <IconButton onClick={() => setTaxToDelete(params.row)} size='small' aria-label='delete'>
              <Delete fontSize='inherit' />
            </IconButton>
          </Box>
        ),
      },
    ];

    return (
      <Card elevation={3}>
        <CardHeader
          title={<Typography fontFamily={'OpenSansMedium'}>{t`Taxes`}</Typography>}
          action={editable && <AddRowBtn onClick={createHandler}/>}
        />
        <Divider/>
        <CardContent>
          <DataGrid
            autoHeight
            density={'compact'}
            getRowId={editable ? ({localId}) => localId : ({id}) => id}
            columns={editable ? editableColumns : readOnlyColumns}
            rows={editable ? nonRefundableTaxes : nonRefundableTaxes.map((tx, idx) => ({
              taxCode: tx.taxCode,
              value: {value: tx.value, currency: tx.currency},
              currentValue: {value: tx.currentValue, currency: tx.currency},
              type: tx.type,
              id: idx
            }))}
            initialState={{pagination: {paginationModel: {pageSize: 100}}}}
            pageSizeOptions={[100]}
            disableRowSelectionOnClick
            hideFooter
            sx={table}
          />
          {Boolean(taxToDelete) && (
            <ConfirmDialog
              title={t`Tax`}
              cancelText={t`Cancel`}
              confirmText={t`Delete`}
              variant={'error'}
              onConfirm={removeHandler}
              onCancel={() => setTaxToDelete(null)}
              reverse
            >
              {t`Do you really want to remove this tax?`}
            </ConfirmDialog>
          )}
        </CardContent>
      </Card>
    );
  }

  const filteredTaxes = taxes
    .map((tx, originalIdx) => ({...tx, _taxIdx: originalIdx}))
    .filter(tx => tx.active !== false);

  const nonManualTypeEditHandler = (taxIdx, newType) => {
    setValue('taxes', taxes.map((tx, idx) => idx === taxIdx ? {...tx, type: newType, modified: true} : tx), {shouldValidate: true});
  };

  const nonManualColumns = [
    readOnlyColumns[0],
    readOnlyColumns[1],
    readOnlyColumns[2],
    {
      field: 'type',
      headerName: t`Type`,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      editable: editable,
      renderCell: (params) => (
        <Box display='flex' alignItems='center' gap={0.5}
          onClick={() => editable && params.api.startCellEditMode({id: params.id, field: params.field})}
          sx={{cursor: editable ? 'pointer' : 'default', width: '100%'}}
        >
          <Typography variant='body2' sx={{fontSize: '0.875rem', flexGrow: 1}}>{params.value}</Typography>
          {editable && <KeyboardArrowDown fontSize='small' sx={{color: 'text.secondary', fontSize: '1rem'}} />}
        </Box>
      ),
      renderEditCell: (params) => (
        <Select
          value={params.value || ''}
          size='small'
          variant='standard'
          disableUnderline
          autoFocus
          fullWidth
          sx={{'& .MuiInputBase-input': {fontSize: '0.875rem', pl: 1.15}}}
          onChange={(e) => {
            params.api.setEditCellValue({id: params.id, field: params.field, value: e.target.value});
            nonManualTypeEditHandler(params.id, e.target.value);
          }}
        >
          {MANUAL_TAX_TYPES.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
        </Select>
      ),
    }
  ];

  return (
    <Card elevation={3}>
      <CardHeader title={<Typography fontFamily={'OpenSansMedium'}>{t`Taxes`}</Typography>}/>
      <Divider/>
      <CardContent>
        <DataGrid
          columns={nonManualColumns}
          rows={filteredTaxes.map((c) => ({
            id: c._taxIdx,
            taxCode: c.taxCode,
            value: {value: c.value, currency: c.currency},
            currentValue: {value: c.currentValue, currency: c.currency},
            type: c.type,
          }))}
          density={'compact'}
          hideFooter
          disableRowSelectionOnClick
          sx={table}
        />
      </CardContent>
    </Card>
  );
};

export default RefundSubjectTaxes;
